import { useState, useEffect, useRef } from 'react';
import * as api from '../../../../api/apiConnector';
import * as utils from '../../../../components/utils';
import { useTranslation } from 'react-i18next';

export const useHandHygiene = (sheetId, currentUser) => {
    const [sheet, setSheet] = useState(null);
    const [toSave, setToSave] = useState(false);
    const [sheetNumber, setSheetNumber] = useState(null);
    const [canEdit, setCanEdit] = useState(true); // Track if user can edit the sheet
    const { t } = useTranslation();

    const [config, setConfig] = useState({});
    const [vocabs, setVocabs] = useState({
        operators: [],
        departmentCategories: [],
        authorities: [],
        departments: [],
        filteredDepartments: [],
        functionalAreas: [],
        filteredAreas: [],
        allUnits: [], // All business units for smart filtering
    });

    const startTimeRef = useRef(null);
    const endTimeRef = useRef(null);

    const normalizeSheet = async (sheet) => {

        const parseDate = (value) => {
            const date = new Date(value);
            if (isNaN(date.getTime())) return null;
            // Reset seconds and milliseconds to ensure clean formatting
            date.setSeconds(0, 0);
            return date;
        };

        sheet.StartObservation = parseDate(sheet.StartObservation);
        sheet.EndObservation = parseDate(sheet.EndObservation);
        sheet.ItemLastUpdatedBy = parseDate(sheet.ItemLastUpdatedBy);
        return sheet
    }

    // Recursively retrieve child areas
    const getDescendants = (parentId, areas) => {
        if (!parentId) return [];
        const children = areas.filter(x => String(x.ParentId) === String(parentId));
        return children.flatMap(child => [
            child,
            ...getDescendants(child.Id, areas)
        ]);
    };

    // Smart filtering: finds all OPUNIT descendants, even if they pass through nodes of different types
    /*     const getSmartDescendants = (parentId, allUnits, targetType) => {
            if (!parentId) return [];
            
            // Get all direct children of this parent (regardless of type)
            const directChildren = allUnits.filter(x => String(x.ParentId) === String(parentId));
            
            const results = [];
            
            for (const child of directChildren) {
                // If this child is of the target type, include it
                if (child.UnitTypeCode === targetType) {
                    results.push(child);
                }
                
                // Always recurse to find deeper descendants of target type
                // This allows us to "skip" intermediate nodes of different types
                const descendants = getSmartDescendants(child.Id, allUnits, targetType);
                results.push(...descendants);
            }
            
            return results;
        }; */

    const getSmartDescendants = (parentId, allUnits, targetType) => {
        if (!parentId) return [];

        const directChildren = allUnits.filter(x => String(x.ParentId) === String(parentId));
        const results = [];

        for (const child of directChildren) {
            if (child.UnitTypeCode === targetType) {
                // Controlla se ha figli dello stesso tipo (anche in profondità)
                const subDescendants = getSmartDescendants(child.Id, allUnits, targetType);

                if (subDescendants.length === 0) {
                    // È una foglia: nessun discendente dello stesso tipo sotto di lui
                    results.push(child);
                } else {
                    // Ha figli dello stesso tipo → skippa lui, prendi solo i figli
                    results.push(...subDescendants);
                }
            } else {
                // Nodo intermedio di tipo diverso → continua a scendere
                const descendants = getSmartDescendants(child.Id, allUnits, targetType);
                results.push(...descendants);
            }
        }

        return results;
    };

    // Update only the filtered departments by authority
    const filterDepartmentsByAuthority = (authorityId) => {
        if (!authorityId) {
            setVocabs(prev => ({
                ...prev,
                filteredDepartments: vocabs.departments,
                filteredAreas: []
            }));
            return;
        }
        const descendants = getDescendants(authorityId, vocabs.departments);
        setVocabs(prev => ({
            ...prev,
            filteredDepartments: descendants,
            filteredAreas: [] // Reset areas when authority changes
        }));
    };

    // Update only the filtered areas - NOW WITH SMART FILTERING
    const filterAreasByDepartment = (departmentId) => {
        if (!departmentId) {
            setVocabs(prev => ({ ...prev, filteredAreas: [] }));
            return;
        }
        // Use smart filtering with ALL units to find OPUNIT descendants, even through nodes of different types
        const descendants = getSmartDescendants(departmentId, vocabs.allUnits, 'OPUNIT');
        setVocabs(prev => ({ ...prev, filteredAreas: descendants }));
    };

    // Initial data loading
    const loadInitialData = async () => {
        (async () => {
            try {
                // Parallelize API calls
                // Execute calls in parallel, but continue even if one fails            
                const results = await Promise.allSettled([
                    await api.sendMessage("darm/handHygiene/getSheet", { user: currentUser(), body: { sheetId } }),
                    await api.sendMessage("darm/configurations/get", {
                        user: currentUser(),
                        body: { forUI: true }
                    }),
                    await api.sendMessage("admin/vocs/get", { user: currentUser(), body: { voc_id: "hand_hygiene_oms_operators" } }),
                    await api.sendMessage("admin/vocs/get", { user: currentUser(), body: { voc_id: "hand_hygiene_oms_departments_category" } }),
                    await api.sendMessage("admin/system/organization/get_business_units", { user: currentUser() }),
                    //api.sendMessage("darm/handHygiene/GetLastSheetNumber", { user: currentUser() })
                ]);

                // Extract sheet data and canEdit flag
                const sheetRes = results[0].status === 'fulfilled' ? results[0].value : { sheet: {}, canEdit: true };
                setCanEdit(sheetRes?.canEdit ?? true); // Set canEdit state from API response
                // Extract custom config
                const configRes = results[1].status === 'fulfilled' ? results[1].value : null;
                // Extract operatorsVoc
                const opsRes = results[2].status === 'fulfilled' ? results[2].value : { items: [] };
                // Extract departmentCategory
                const catRes = results[3].status === 'fulfilled' ? results[3].value : { items: [] };
                // Extract business units
                const buRes = results[4].status === 'fulfilled' ? results[4].value : { units: [] };
                // Extract last sheet number
                // const numRes = results[5].status === 'fulfilled' ? results[5].value : { success: false };
                //Get rawConfig
                const rawConfig = configRes?.data?.config;
                //Parse rawConfig
                // setConfig(rawConfig ? JSON.parse(rawConfig) : {});
                setConfig(rawConfig ? rawConfig : {});

                // Vocabs
                const operators = opsRes?.items ?? [];
                const categories = catRes?.items ?? [];
                const units = buRes?.units ?? [];
                const departments = units.filter(u => u.UnitTypeCode === 'DEP');
                const functionalAreas = units.filter(u => u.UnitTypeCode === 'OPUNIT');
                const authorities = units.filter(u => u.UnitTypeCode === 'AUT');

                // Sheet (cloned to avoid mutations)
                const sheetData = utils.clone(sheetRes?.sheet || { ObservedOperators: [] });

                // If this is a new sheet without StartObservation, set it to current time
                if (!sheetData.StartObservation) {
                    const now = new Date();
                    // Reset seconds and milliseconds for clean formatting
                    now.setSeconds(0, 0);
                    sheetData.StartObservation = now.toISOString();
                    sheetData.ObservationDate = sheetData.ObservationDate || now.toISOString().split("T")[0];
                }

                const normSheet = await normalizeSheet(sheetData);

                const sheetClearData = utils.clone(normSheet);

                // Order cloned ObservedOperators and related Opportunities
                sheetClearData.ObservedOperators = Array.isArray(sheetClearData.ObservedOperators)
                    ? sheetClearData.ObservedOperators
                        .map(op => ({
                            ...op,
                            Opportunities: Array.isArray(op.Opportunities)
                                ? [...op.Opportunities].sort((a, b) => a.Observation_items_order - b.Observation_items_order)
                                : []
                        }))
                        .sort((a, b) => a.Observation_items_order - b.Observation_items_order)
                    : [];

                // Calculate initial filtered departments if there is already an authority
                const initialFilteredDepartments = sheetClearData.HealthAuthority
                    ? getDescendants(sheetClearData.HealthAuthority, departments)
                    : departments; // If no authority, show all departments

                // Calculate initial child areas if there is already a department - NOW WITH SMART FILTERING
                const initialFilteredAreas = sheetClearData.Department
                    ? getSmartDescendants(sheetClearData.Department, units, 'OPUNIT')
                    : [];

                setVocabs({
                    operators,
                    departmentCategories: categories,
                    authorities,
                    departments,
                    filteredDepartments: initialFilteredDepartments,
                    functionalAreas,
                    filteredAreas: initialFilteredAreas,
                    allUnits: units // Store all units for smart filtering
                });
                setSheet(sheetClearData);

                // Sheet number
                if (sheetId !== null) {
                    // // New sheet: use next available number
                    // if (numRes?.success) {
                    //     setSheetNumber(numRes.lastNumber + 1);
                    // } else {
                    //     setSheetNumber(1); // Fallback for new sheets
                    // }
                    //} else {
                    // Existing sheet: use the sheet's own sequential number
                    setSheetNumber(sheetClearData.SequentialNumber || null);
                }

            } catch (err) {
                console.error('Error loading hand hygiene:', err);
            }
        })();
    };

    // Initial effect
    useEffect(() => {
        (async () => {
            await loadInitialData();
        })();
    }, [sheetId, currentUser]);

    // Update filtered departments when authority changes
    useEffect(() => {
        if (sheet?.HealthAuthority && vocabs.departments.length) {
            filterDepartmentsByAuthority(sheet.HealthAuthority);
        }
    }, [sheet?.HealthAuthority, vocabs.departments]);

    // Update filtered areas when department changes
    useEffect(() => {
        if (sheet?.Department && vocabs.functionalAreas.length) {
            filterAreasByDepartment(sheet.Department);
        }
    }, [sheet?.Department, vocabs.functionalAreas]);

    // Update sheet
    const updateSheet = (updated) => {
        setToSave(true);
        setSheet(utils.clone(updated));
    };

    return {
        sheet,
        updateSheet,
        toSave,
        sheetNumber,
        vocabs,
        config,
        canEdit, // Return canEdit flag
        filterAreasByDepartment,
        filterDepartmentsByAuthority,
        startTimeRef,
        endTimeRef
    };
};
